/**
 * Accessibility Evaluation and Improvements:
 *
 * To improve accessibility (a11y) for all fields in this FormField component, you should:
 * 1. Ensure all form controls have associated labels using `aria-label`, `aria-labelledby`, or native `label` elements.
 * 2. Use `aria-describedby` to associate helper text and error messages with the input.
 * 3. Use unique IDs for each input, label, helper text, and error message.
 * 4. Pass these ARIA attributes to the underlying UI components (Input, TextArea, Checkbox, etc.).
 *
 * Below is a rewrite that adds ARIA attributes for accessibility.
 */

import type { LorebookVisibilitySource } from "@next-platform/client-common-core/lorebook/useLorebookVisibilityConfirmation";
import getNormalizedCountLabel from "@next-platform/client-common-core/utils/getNormalizedCountLabel";
import { Checkbox, Input, TagInput, Text, TextArea, TextVariants } from "@next-platform/client-common-spicy-ui";
import { type ComponentType, createElement, type ReactNode, type SyntheticEvent, useCallback, useId, useMemo } from "react";
import { type FieldErrors, useController } from "react-hook-form";

import { MAXIMUM_TAG_LIMIT } from "@/constants";
import AvatarCreate from "@/pages/CreateCharacter/v2/components/AvatarCreate";

import applyNormalizedFieldChange from "../utils/applyNormalizedFieldChange";
import convertStringsToTags from "../utils/convertStringsToTags";
import convertTagsToStrings from "../utils/convertTagsToStrings";
import getFieldErrorMessage from "../utils/getFieldErrorMessage";
import LorebookVisibilityField from "./LorebookVisibilityField";
import TagsField from "./TagsField";

interface IFormFieldProps {
  lorebook?: LorebookVisibilitySource;
  name: string;
  type: string;
  errors: FieldErrors;
  label: string | ReactNode;
  placeholder?: string;
  helperText?: string;
  maxLength?: number;
  required?: boolean;

  control: any;
  tooltipLabel?: string;
  maxCount?: number;
  disabled?: boolean;
  readOnly?: boolean;
}

/** Shared context passed from FormField to each field type renderer */
interface IFieldRenderContext {
  value: unknown;
  onChange: (...event: unknown[]) => void;
  errorMessage: string;
  inputId: string;
  labelId: string;
  helperId: string;
  errorId: string;
  ariaDescribedBy: string;
  disabled: boolean;
  fieldType: string;
  name: string;
  label: string | ReactNode;
  placeholder?: string;
  helperText?: string;
  maxLength?: number;
  required?: boolean;
  tooltipLabel?: string;
  maxCount?: number;
  readOnly?: boolean;
}

function TagsFormField({
  labelId,
  inputId,
  helperId,
  errorId,
  ariaDescribedBy,
  value,
  onChange,
  label,
  helperText,
  placeholder,
  required,
  errorMessage,
  disabled,
  readOnly,
}: IFieldRenderContext) {
  const handleTagsChange = useCallback(
    (tagArray: { label: string; value: string }[]) => {
      onChange(convertTagsToStrings(tagArray));
    },
    [onChange],
  );

  return (
    <div>
      {label ? (
        <Text id={labelId} variant={TextVariants.LABEL_LARGE_MEDIUM} as="label" htmlFor={inputId}>
          {label}
          {required ? <span className="text-red-10">*</span> : null}
        </Text>
      ) : null}

      <TagsField
        aria-labelledby={labelId}
        aria-describedby={ariaDescribedBy || undefined}
        disabled={disabled}
        initialValues={convertStringsToTags((value as string[]) || [])}
        {...(placeholder && { placeholder })}
        setSelectedTags={handleTagsChange}
        error={!!errorMessage}
        readOnly={readOnly}
      />
      <div className="flex justify-between">
        {!!errorMessage && (
          <Text id={errorId} variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-9" role="alert" aria-live="polite">
            {errorMessage}
          </Text>
        )}
        {!errorMessage && helperText ? (
          <Text id={helperId} variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
            {helperText}
          </Text>
        ) : null}
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
          {(value as string[])?.length || 0}/{MAXIMUM_TAG_LIMIT}
        </Text>
      </div>
    </div>
  );
}

function AvatarFormField({
  labelId,
  inputId,
  helperId,
  errorId,
  value,
  onChange,
  label,
  helperText,
  required,
  errorMessage,
  disabled,
}: IFieldRenderContext) {
  const handleAvatarChange = useCallback(
    (avatarUrl: string) => {
      onChange(avatarUrl);
    },
    [onChange],
  );

  return (
    <div className="flex flex-col gap-md">
      {label ? (
        <Text id={labelId} variant={TextVariants.LABEL_LARGE_MEDIUM} as="label" htmlFor={inputId}>
          {label}
          {required ? <span className="text-red-10">*</span> : null}
        </Text>
      ) : null}
      <AvatarCreate disabled={disabled} characterAvatar={value as string} setCharacterAvatar={handleAvatarChange} />
      {!!errorMessage && (
        <Text id={errorId} variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-9" role="alert" aria-live="polite">
          {errorMessage}
        </Text>
      )}
      {!errorMessage && helperText ? (
        <Text id={helperId} variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
          {helperText}
        </Text>
      ) : null}
    </div>
  );
}

function CheckboxFormField({
  inputId,
  labelId,
  ariaDescribedBy,
  errorId,
  value,
  onChange,
  label,
  name,
  errorMessage,
  disabled,
}: IFieldRenderContext) {
  const handleCheckboxChange = useCallback((event: SyntheticEvent) => onChange((event.target as HTMLInputElement).checked ?? false), [onChange]);

  return (
    <>
      <Checkbox
        id={inputId}
        aria-labelledby={labelId}
        aria-describedby={ariaDescribedBy || undefined}
        label={
          <Text id={labelId} variant={TextVariants.LABEL_LARGE_MEDIUM}>
            {label}
          </Text>
        }
        name={name}
        checked={value as boolean}
        disabled={disabled}
        onChange={handleCheckboxChange}
      />
      {!!errorMessage && (
        <Text id={errorId} variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-9" role="alert" aria-live="polite">
          {errorMessage}
        </Text>
      )}
    </>
  );
}

function TextareaFormField({
  inputId,
  labelId,
  ariaDescribedBy,
  value,
  onChange,
  label,
  name,
  placeholder,
  helperText,
  maxLength,
  required,
  errorMessage,
  tooltipLabel,
  disabled,
  readOnly,
}: IFieldRenderContext) {
  const handleChange = useCallback(
    (inputValue: string) => {
      applyNormalizedFieldChange({ inputValue, maxLength, onChange });
    },
    [maxLength, onChange],
  );
  const countLabel = getNormalizedCountLabel({ value, maxLength });

  return (
    <TextArea
      id={inputId}
      aria-labelledby={labelId}
      aria-describedby={ariaDescribedBy || undefined}
      label={label as string}
      name={name}
      value={(value as string) || ""}
      onChange={handleChange}
      placeholder={placeholder}
      helperText={helperText}
      countLabel={countLabel}
      showMaxLengthLabel={!maxLength}
      required={required}
      error={!!errorMessage}
      errorMessage={errorMessage || undefined}
      tooltipLabel={tooltipLabel}
      disabled={disabled}
      readOnly={readOnly}
    />
  );
}

function KeywordsFormField({
  labelId,
  ariaDescribedBy,
  value,
  onChange,
  label,
  placeholder,
  helperText,
  errorMessage,
  tooltipLabel,
  maxCount,
  required,
  disabled,
  readOnly,
}: IFieldRenderContext) {
  const selectedValues = useMemo(() => (value as string[]) || [], [value]);
  const handleKeywordsChange = useCallback(
    (values: string[]) => {
      onChange(values);
    },
    [onChange],
  );

  return (
    <div className="[&_button]:!h-auto [&_button]:!min-h-[32px] [&_button]:!max-w-full [&_button]:!py-1 [&_button>span]:!whitespace-normal [&_button>span]:!break-all [&_button>span]:text-left">
      <TagInput
        aria-labelledby={labelId}
        aria-describedby={ariaDescribedBy || undefined}
        label={label as string}
        selectedValues={selectedValues}
        onChange={handleKeywordsChange}
        placeholder={placeholder}
        tooltipLabel={tooltipLabel}
        helperText={helperText}
        error={!!errorMessage}
        errorMessage={errorMessage || undefined}
        maxCount={maxCount}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
      />
    </div>
  );
}

function DefaultFormField({
  inputId,
  labelId,
  ariaDescribedBy,
  fieldType,
  value,
  onChange,
  name,
  label,
  placeholder,
  helperText,
  maxLength,
  required,
  errorMessage,
  tooltipLabel,
  disabled,
  readOnly,
}: IFieldRenderContext) {
  const handleChange = useCallback(
    (inputValue: string) => {
      applyNormalizedFieldChange({ inputValue, maxLength, onChange });
    },
    [maxLength, onChange],
  );
  const countLabel = getNormalizedCountLabel({ value, maxLength });

  return (
    <Input
      id={inputId}
      aria-labelledby={labelId}
      aria-describedby={ariaDescribedBy || undefined}
      type={fieldType}
      label={label as string}
      name={name}
      value={(value as string) || ""}
      countLabel={countLabel}
      showMaxLengthLabel={!maxLength}
      required={required}
      placeholder={placeholder}
      helperText={helperText}
      onChange={handleChange}
      error={!!errorMessage}
      errorMessage={errorMessage || undefined}
      tooltipLabel={tooltipLabel}
      disabled={disabled}
      readOnly={readOnly}
    />
  );
}

const FIELD_RENDERERS: Record<string, ComponentType<IFieldRenderContext>> = {
  tags: TagsFormField,
  avatar: AvatarFormField,
  checkbox: CheckboxFormField,
  textarea: TextareaFormField,
  keywords: KeywordsFormField,
};

export const FormField = ({ name, type, control, errors, tooltipLabel, disabled = false, readOnly = false, ...props }: IFormFieldProps) => {
  const { field } = useController({ name, control });
  const { value, onChange } = field;
  const errorMessage = getFieldErrorMessage(errors, name) ?? "";
  const reactId = useId();
  const inputId = `${name}-input-${reactId}`;
  const labelId = `${name}-label-${reactId}`;
  const helperId = `${name}-helper-${reactId}`;
  const errorId = `${name}-error-${reactId}`;

  let ariaDescribedBy = "";
  if (errorMessage) ariaDescribedBy += errorId;
  if (props.helperText && !errorMessage) ariaDescribedBy += (ariaDescribedBy ? " " : "") + helperId;

  const context: IFieldRenderContext = {
    value,
    onChange,
    errorMessage,
    inputId,
    labelId,
    helperId,
    errorId,
    ariaDescribedBy,
    disabled,
    fieldType: type,
    name,
    label: props.label,
    placeholder: props.placeholder,
    helperText: props.helperText,
    maxLength: props.maxLength,
    required: props.required,
    tooltipLabel,
    maxCount: props.maxCount,
    readOnly,
  };

  if (type === "visibility") {
    return <LorebookVisibilityField {...context} value={value} lorebook={props.lorebook} />;
  }

  const fieldRenderer = FIELD_RENDERERS[type] ?? DefaultFormField;
  return <div data-field-name={name}>{createElement(fieldRenderer, context)}</div>;
};
